import {
  IonGrid,
  IonCol,
  IonRow,
  IonContent,
  IonIcon,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonReorderGroup,
  IonItem,
  IonReorder,
  IonFab,
  IonFabButton,
  useIonViewWillEnter,
  IonItemSliding,
  IonItemOptions,
  IonItemOption,
  IonList,
  isPlatform
} from '@ionic/react';
import { addSharp, chevronBack, chevronForward, trashBin } from 'ionicons/icons';
import CloudSyncIcon from '../../assets/images/cloud-sync-outline.svg'
import './MyPrayersPage.scss';
import React, { useState } from 'react';
import PageHeader from '../../components/PageHeader/PageHeader';
import IRouteInfo from '../../interfaces/IRouteInfo';
import { PrayerStatus, PrayerStatusValues, PrayerStatusNames } from '../../data/PrayerStatus';
import { useHistory } from 'react-router-dom';
import { AppRouteKeys, AppRouteInfo } from '../../routes/AppRoutes';
import { ItemReorderEventDetail } from '@ionic/core';
import IPrayer from '../../interfaces/IPrayer';
import PrayerService from '../../services/PrayerService';
import { UserRouteInfo, UserRouteKeys } from '../../routes/UserRoutes';

const Prayer: React.FC<IRouteInfo> = (props) => {

  const history = useHistory();
  const [selectPrayerStatus, setSelectPrayerStatus] = useState<PrayerStatusValues | string>(PrayerStatus.active);
  const [myPrayerListActiveIds, setMyPrayerListActiveIds] = useState<number[]>([]);
  const [activePrayers, setActivePrayers] = useState<(IPrayer | undefined)[]>([]);
  const [revisitPrayers, setRevisitPrayers] = useState<(IPrayer | undefined)[]>([]);
  const [answeredPrayers, setAnsweredPrayers] = useState<(IPrayer | undefined)[]>([]);

  useIonViewWillEnter(() => {
    loadPrayers();
  });

  const loadPrayers = async () => {
    const result = await PrayerService.getAllPrayers();
    if(result.Success) {
      const userPrayers = result.Data!;
      const userActivePrayers = userPrayers.filter(prayer => prayer?.status === PrayerStatus.active);
      const userRevisitPrayers = userPrayers
        .filter(prayer => prayer?.status === PrayerStatus.revisit)
        .sort((a, b) => 
          a.modifiedDate! < b.modifiedDate! ? 1 :
          a.modifiedDate! > b.modifiedDate! ? -1 : 0);
      const userAnsweredPrayers = userPrayers
        .filter(prayer => prayer?.status === PrayerStatus.answered)
        .sort((a, b) => 
          a.modifiedDate! < b.modifiedDate! ? 1 :
          a.modifiedDate! > b.modifiedDate! ? -1 : 0);
      setMyPrayerListActiveIds(userActivePrayers.map(prayer => prayer.id));
      setActivePrayers(userActivePrayers);
      setRevisitPrayers(userRevisitPrayers);
      setAnsweredPrayers(userAnsweredPrayers);
    }
  }

  const updatePrayer = (prayer: IPrayer) => {
    PrayerService.updatePrayer(prayer).then(result => {
      if(result.Success) {
        loadPrayers();
      }
    });
  }

  const deletePrayer = (prayer: IPrayer) => {
    // We don't completely remove prayer record so that cloud sync will work
    const deletedPrayer = {...prayer, 
      title: '',
      description: '',
      answer: '',
      note: '',
      status: PrayerStatus.deleted,
      modifiedDate: new Date(Date.now()),
    }
    PrayerService.updatePrayer(deletedPrayer).then(result => {
      if(result.Success) {
        loadPrayers();
      }
    });
  }

  const changeDisplayStatus = async (status: PrayerStatusValues) => {
    setSelectPrayerStatus(status);
  }

  function doReorder(event: CustomEvent<ItemReorderEventDetail>) {
    // Before complete is called with the items they will remain in the
    // order before the drag
    console.log('Before complete', myPrayerListActiveIds);

    // Finish the reorder and position the item in the DOM based on
    // where the gesture ended. Update the items variable to the
    // new order of items
    const myPrayerListIdsEdited: number[] = event.detail.complete(myPrayerListActiveIds);
    const orderedPrayers = myPrayerListIdsEdited.map((id, index) => {
      const orderedPrayer: IPrayer = activePrayers.find(prayer => prayer?.id === id)!;
      orderedPrayer.order = index;
      return orderedPrayer;
    });

    // After complete is called the items will be in the new order
    setMyPrayerListActiveIds(myPrayerListIdsEdited);
    //setMyPrayerList([...orderedPrayers, ...nonActivePrayers]);
    setActivePrayers([...orderedPrayers]);
    PrayerService.updatePrayers(orderedPrayers).then(result => {
      if(!result.Success){
        console.log("Error", result.Message.UserMessage);
      }
    });
    console.log('After complete', myPrayerListActiveIds);
  }

  const renderPrayerList = (prayerStatus: PrayerStatusValues) => {
    switch (prayerStatus) {
      case PrayerStatus.active:
        return <IonReorderGroup disabled={false} onIonItemReorder={doReorder} className="my-prayers-list">
          { activePrayers.map(prayer => (
            <IonItemSliding key={prayer?.id} className={isPlatform("ios") ? "my-prayer-item-slide-ios" : "my-prayer-item-slide"}> 
              <IonItemOptions side="start">
                <IonItemOption color="secondary" onClick={() => prayer && updatePrayer({...prayer, status: PrayerStatus.revisit})}>
                  <IonIcon icon={chevronBack} slot="start" /> MOVE TO REVISIT
                </IonItemOption>
              </IonItemOptions>
              <IonItem className="prayer-list-item">
                <IonLabel onClick={
                  () => history.push(
                    AppRouteInfo[AppRouteKeys.PrayerUpdate].path.replace(':id', 
                      prayer?.id || prayer?.id === 0 ? prayer?.id.toString(): ''), 
                    {direction: 'forward'})
                }>{prayer?.title}</IonLabel>
                <IonReorder slot="end" />
              </IonItem>
              <IonItemOptions side="end">
                <IonItemOption color="secondary" onClick={() => prayer && updatePrayer({...prayer, status: PrayerStatus.answered})}>
                  MOVE TO ANSWERED <IonIcon icon={chevronForward} slot="end" />
                </IonItemOption>
              </IonItemOptions>
            </IonItemSliding>
          ))}
        </IonReorderGroup>;
      case PrayerStatus.revisit:
        return <IonList className={"my-prayers-list" + isPlatform("ios") ? "my-prayer-item-slide-ios" : "my-prayer-item-slide"}>
          { revisitPrayers.map(prayer => (
            <IonItemSliding disabled={false} key={prayer?.id}>
              <IonItemOptions side="start">
                <IonItemOption color="danger" onClick={() => prayer && deletePrayer(prayer)}>
                  <IonIcon icon={trashBin} slot="start" /> DELETE PRAYER
                </IonItemOption>
              </IonItemOptions>
              <IonItem className="prayer-list-item">
                <IonLabel onClick={
                  () => history.push(
                    AppRouteInfo[AppRouteKeys.PrayerUpdate].path.replace(':id', 
                      prayer?.id || prayer?.id === 0 ? prayer?.id.toString(): ''), 
                    {direction: 'forward'})
                }>{prayer?.title}</IonLabel>
              </IonItem>
              <IonItemOptions side="end">
                <IonItemOption color="secondary" onClick={() => prayer && updatePrayer({...prayer, status: PrayerStatus.active})}>
                  MOVE TO ACTIVE <IonIcon icon={chevronForward} slot="end" />
                </IonItemOption>
              </IonItemOptions>
            </IonItemSliding>
          ))}
        </IonList>
      case PrayerStatus.answered:
        return <IonList className={"my-prayers-list" + isPlatform("ios") ? "my-prayer-item-slide-ios" : "my-prayer-item-slide"}>
          { answeredPrayers.map(prayer => (
            <IonItemSliding key={prayer?.id}>
              <IonItemOptions side="start">
                <IonItemOption color="secondary" onClick={() => prayer && updatePrayer({...prayer, status: PrayerStatus.active})}>
                  <IonIcon icon={chevronBack} slot="start" /> MOVE TO ACTIVE
                </IonItemOption>
              </IonItemOptions>
              <IonItem className="prayer-list-item">
                <IonLabel onClick={
                  () => history.push(
                    AppRouteInfo[AppRouteKeys.PrayerUpdate].path.replace(':id', 
                      prayer?.id || prayer?.id === 0 ? prayer?.id.toString(): ''), 
                    {direction: 'forward'})
                }>{prayer?.title}</IonLabel>
              </IonItem>
              <IonItemOptions side="end">
                <IonItemOption color="danger" onClick={() => prayer && deletePrayer(prayer)}>
                  <IonIcon icon={trashBin} slot="end" /> DELETE PRAYER
                </IonItemOption>
              </IonItemOptions>
            </IonItemSliding>      
          ))}
        </IonList>;
    }
  }

  return (
    <IonPage>
      <PageHeader {...props} showBackButton={true} menuSrc={CloudSyncIcon} navLink={UserRouteInfo[UserRouteKeys.SyncUserData].path} ></PageHeader>
      <IonContent color="prayer-shade">
        <IonGrid fixed={true} className="my-prayers-page-grid">
          <IonRow className="my-prayers-status-selector">
            <IonCol className="my-prayers-status-selector">
              <IonSegment mode="md" value={selectPrayerStatus}
                onIonChange={e => (e.detail.value?.toString() ? changeDisplayStatus(e.detail.value?.toString() as PrayerStatusValues) : '')}
                color={AppRouteInfo[AppRouteKeys.MyPrayers].themeColorName}
                className="pray-status-selector">
                <IonSegmentButton value={PrayerStatus.revisit}>
                  <IonLabel>{PrayerStatusNames[PrayerStatus.revisit]}</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value={PrayerStatus.active}>
                  <IonLabel>{PrayerStatusNames[PrayerStatus.active]}</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value={PrayerStatus.answered}>
                  <IonLabel>{PrayerStatusNames[PrayerStatus.answered]}</IonLabel>
                </IonSegmentButton>
              </IonSegment>
            </IonCol>
          </IonRow>
          <IonRow className="ion-text-center">
            <IonCol>
              {renderPrayerList(selectPrayerStatus as PrayerStatusValues)}
            </IonCol>
          </IonRow>
        </IonGrid>
        <IonFab vertical="bottom" horizontal="end" slot="fixed">
          <IonFabButton color={AppRouteInfo[AppRouteKeys.PrayerUpdate].themeColorName} onClick={() => history.push(AppRouteInfo[AppRouteKeys.PrayerUpdate].path.replace(':id', 'new'), {direction: 'forward'})}>
            <IonIcon color="light" size="large" icon={addSharp} />
          </IonFabButton>
        </IonFab>
      </IonContent>
    </IonPage>
  );
};

export default Prayer;
